<template>
  <div class="search-resource-preview">
    <!-- Lesson Preview -->
    <template v-if="isLessonType">
      <lesson-preview
        :params="params"
        :show-notes="showNotes"
        :show-info-header="showInfoHeader"
        :show-info-content="showInfoContent"
      />
    </template>

    <!-- Media Preview -->
    <template v-if="isMediaType">
      <div class="header">
        <match-indicator v-if="hasMatchMediaGeneral" />
        <h5 class="media-title">{{ media.title }}</h5>
        <match-indicator v-if="hasMatchMediaTranscription" />
      </div>
      <media-component
        ref="media"
        :uuid="media.uuid"
        type="mo"
        :show-ai-info-icon="false"
        :zoom-tools="true"
        :restriction="false"
        :copyright="false"
        :open-in-new-tab="false"
        :mediatype="media.fileType"
        :is-inline="false"
        :is-transcript-enabled="true"
        :render-transcript="true"
        :transcript-start-time="transcriptStartTime"
      />
      <rich-text :expandable="false" :content="media.description" />
    </template>

    <!-- Question Preview -->
    <template v-if="isQuestionType">
      <div v-if="!isMpqQuestion" class="component question-component">
        <!-- Recommended Time -->
        <dl v-if="showRecommendedTime" class="field-set recommended-time">
          <dt class="field-label">
            {{ $t("question_component_recommended_time_label") }}
            <svg-icon name="Time16" kind="indicative" />
          </dt>
          <dd class="field-value">{{ recommendedTime }}</dd>
        </dl>
        <!-- Question View -->
        <free-response-view
          v-if="isFreeResponse"
          :key="question.id"
          mode="VIEW"
          :highlights="matchQuestion"
          :question="question"
        >
          <template #grading-rubric>
            <grading-rubric-view
              v-if="hasGradingRubric"
              :grading-rubric="gradingRubric"
              :question="question"
              :show-mark-range="question.showMarks"
              :is-from-search-resource-preview="true"
            />
          </template>
        </free-response-view>
        <multiple-choice-view
          v-if="isMultipleChoice"
          :key="question.id"
          :highlights="matchQuestion"
          :question="question"
        />
        <fill-in-blank-view
          v-if="isFillInBlank"
          :key="question.id"
          :highlights="matchQuestion"
          :question="question"
        />
        <audio-response-view
          v-if="isAudioResponse"
          :key="question.id"
          mode="VIEW"
          :highlights="matchQuestion"
          :question="question"
        >
          <template #grading-rubric>
            <grading-rubric-view
              v-if="hasGradingRubric"
              :grading-rubric="gradingRubric"
              :question="question"
              :show-mark-range="question.showMarks"
              :is-from-search-resource-preview="true"
            />
          </template>
        </audio-response-view>
        <error-editing-view
          v-if="isErrorEditing"
          :key="question.id"
          :highlights="matchQuestion"
          :question="question"
        />
        <drag-drop-view
          v-if="isDragDrop"
          :key="question.id"
          :highlights="matchQuestion"
          :question="question"
        />

        <!-- Tries (Only for non open ended quesstions) -->
        <dl v-if="showTries" class="field-set tries">
          <dt class="field-label">{{ $t("question_component_tries_view_label") }}</dt>
          <dd class="field-value">{{ tries }}</dd>
        </dl>
        <!-- Mark -->
        <dl v-if="showMarks" class="field-set marks">
          <dt class="field-label">{{ $t("question_component_marks_label") }}</dt>
          <dd class="field-value">[{{ marks }}]</dd>
        </dl>
      </div>

      <div v-else class="mpq-question">
        <match-indicator v-if="matchQuestion" />
        <dl class="field-set type-text question-body">
          <dd class="field-value">
            <rich-text :content="question.questionBody" :expandable="true" :expanded="true" />
          </dd>
        </dl>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { QUESTION_TYPE, NAMESPACE } from "@modules/search/service/search.constant";
import { MEDIA_TYPE } from "@/plugins/media/media.constant";
import DateTimeFormatter from "@utils/datetime.formatter";
import FreeResponseView from "@modules/editor/components/question/FreeResponseView.vue";
import MultipleChoiceView from "@modules/editor/components/question/MultipleChoiceView.vue";
import FillInBlankView from "@modules/editor/components/question/FillInBlankView.vue";
import AudioResponseView from "@modules/editor/components/question/AudioResponseView.vue";
import ErrorEditingView from "@modules/editor/components/question/ErrorEditingView.vue";
import DragDropView from "@modules/editor/components/question/DragDropView.vue";
import GradingRubricView from "@modules/editor/components/grading-rubric/GradingRubricView.vue";
import { isEmpty } from "@utils/var.check";
import { mapGetters } from "vuex";
import LessonPreview from "@plugins/resource/LessonPreview.vue";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";

export default {
  name: "SearchResourcePreview",
  components: {
    MediaComponent,
    LessonPreview,
    FreeResponseView,
    MultipleChoiceView,
    FillInBlankView,
    AudioResponseView,
    ErrorEditingView,
    DragDropView,
    GradingRubricView,
  },
  provide() {
    return {
      resourceContext: {
        id: this.params.uuid,
        teamResponse: () => {
          return false;
        },
      },
    };
  },
  props: {
    params: { type: Object, default: null },
    showNotes: { type: Boolean, default: false },
    showInfoHeader: { type: Boolean, default: true },
    showInfoContent: { type: Boolean, default: true },
  },
  data() {
    return {
      isLoading: true,
    };
  },
  computed: {
    ...mapGetters(NAMESPACE.AUTH, ["isStudent"]),
    isMediaType() {
      return this.params.type === "MEDIA";
    },
    isQuestionType() {
      return this.params.type === "QUESTION";
    },
    isLessonType() {
      return this.params.type === "LESSON" || this.params.type === "ASSIGNMENT_LESSON";
    },
    lesson() {
      return this.params.lesson;
    },
    media() {
      return this.params.mediaObject;
    },
    question() {
      return this.params.question;
    },
    gradingRubric() {
      return this.params.gradingRubric;
    },
    previewable() {
      return (
        this.media.fileType === MEDIA_TYPE.VIDEO ||
        this.media.fileType === MEDIA_TYPE.AUDIO ||
        this.media.fileType === MEDIA_TYPE.HTML5
      );
    },
    isErrorEditing() {
      return this.question.questionType === QUESTION_TYPE.EEQ;
    },
    isDragDrop() {
      return this.question.questionType === QUESTION_TYPE.DND;
    },
    isFillInBlank() {
      return this.question.questionType === QUESTION_TYPE.FILL;
    },
    isFreeResponse() {
      return (
        this.question.questionType === QUESTION_TYPE.FREE ||
        this.question.questionType === QUESTION_TYPE.FILE
      );
    },
    isMultipleChoice() {
      return (
        this.question.questionType === QUESTION_TYPE.MCQ ||
        this.question.questionType === QUESTION_TYPE.MRQ
      );
    },
    isAudioResponse() {
      return this.question.questionType === QUESTION_TYPE.REC;
    },
    isOpenEndedQuestion() {
      return (
        this.question.questionType === QUESTION_TYPE.FREE ||
        this.question.questionType === QUESTION_TYPE.REC ||
        this.question.questionType === QUESTION_TYPE.FILE
      );
    },
    marks() {
      if (!this.question) return 1;
      return this.question.marks;
    },
    transcriptStartTime() {
      if (this.params?.highlights?.transcript?.startTime === undefined) {
        return 0;
      }
      return Math.ceil(this.params.highlights.transcript.startTime) / 1000;
    },
    recommendedTime() {
      if (!this.question) return 0;
      return DateTimeFormatter.formatTimeInSecondToMinuteSecond(this.question.recommendedTime);
    },
    showRecommendedTime() {
      return this.recommendedTime;
    },
    tries() {
      if (!this.question) return 1;
      return this.question.noOfAttempt;
    },
    showTries() {
      return !this.isOpenEndedQuestion && (this.question?.hasCorrectAnswer ?? true);
    },
    showMarks() {
      return this.marks !== 0;
    },
    hasGradingRubric() {
      return !isEmpty(this.gradingRubric);
    },
    activityPageNo() {
      return "";
    },
    isMpqQuestion() {
      return this.question.questionType === QUESTION_TYPE.MPQ;
    },
    hasMatchMediaGeneral() {
      if (isEmpty(this.params.highlights)) {
        return false;
      }
      return !isEmpty(this.params.highlights.title) || !isEmpty(this.params.highlights.description);
    },
    hasMatchMediaTranscription() {
      if (isEmpty(this.params.highlights)) {
        return false;
      }
      return !isEmpty(this.params.highlights.transcript);
    },
    matchQuestion() {
      return isEmpty(this.params.highlights)
        ? null
        : this.params.highlights.title ||
            this.params.highlights.answerValue ||
            this.params.highlights.defaultAnswer ||
            this.params.highlights.contentMap ||
            this.params.highlights.skillLevel ||
            this.params.highlights.answer;
    },
  },
};
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/abstracts/mixins";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.search-resource-preview {
  .header {
    display: flex;
    flex-flow: column nowrap;
  }

  .rich-text-card {
    .header {
      flex-flow: row;
    }
  }

  .match-indicator {
    margin-bottom: $spacing-02;
  }

  .media-title {
    @include carbon--type-style("productive-heading-02", true);
    @include line-clamp(2);
    margin-bottom: $spacing-05;
  }

  .media {
    margin: 0 0 $spacing-05 0;
    &[data-media-type="IMAGE"] {
      .attachment-placeholder {
        flex-flow: row wrap;
        padding: 0;

        > .icon {
          margin: 0 $spacing-03;
        }

        > .title {
          padding: $spacing-03 0;
          max-width: calc(100% - 4rem);
        }

        .tools {
          flex: 0 0 100%;
          border-top: $divider-02;
          padding: $spacing-03 $spacing-03 $spacing-03 $spacing-02;
        }
      }
    }

    .attachment-placeholder {
      flex-flow: row nowrap;

      > .title {
        max-width: 100%;
      }

      > .tools {
        flex: 0 1 auto;
        margin-left: auto;
      }
    }
  }

  .question-component {
    .bx--form {
      margin-top: 0;
    }

    .field-set {
      &.answer-options {
        > .field-label {
          @include carbon--type-style("productive-heading-01", true);
        }

        > .field-input {
          position: relative;
        }

        .option-edit {
          padding: $spacing-05;
          background-color: $ui-background;
          border-radius: 10px;
        }
      }

      &.recommended-time {
        display: flex;
        justify-content: flex-end;
        margin-bottom: $spacing-06;
        .field-label {
          @include carbon--type-style("productive-heading-01", true);
          display: flex;
          align-items: center;
          color: $text-02;
          margin-right: $spacing-03;
          text-transform: uppercase;
        }

        .field-value {
          @include carbon--type-style("productive-heading-01", true);
        }

        .bx--label {
          @include carbon--type-style("body-short-01", true);
        }

        @include media("mobile") {
          flex-flow: row wrap;
          justify-content: flex-start;
        }
      }
      &.type-number {
        .bx--number {
          flex-flow: row nowrap;
          align-items: center;
          > .bx--label {
            &:not(:empty) {
              order: 2;
              margin: 0 $spacing-03;
            }
          }
          > .bx--number__input-wrapper {
            width: 5rem;
            > input {
              min-width: 5rem;
            }
          }
        }
      }

      &.type-radio {
        .option-set {
          .icon {
            border-radius: 50%;
          }
        }
      }

      &.answer-info {
        .answer-info-content {
          margin: $spacing-05 0;
        }
      }

      &.marks,
      &.tries,
      &.option-marks {
        display: flex;
        flex-flow: row wrap;
        align-items: center;
        justify-content: flex-end;
        margin-bottom: $spacing-03;
        text-align: right;
        word-break: normal;
        @include carbon--type-style("label-01", true);
        font-weight: 700;

        .field-label {
          margin-right: $spacing-05;
          color: $text-02;
          text-transform: uppercase;
          font-weight: 700;
        }

        .field-value {
          flex: 0 0 $spacing-09;
          margin: 0;
          padding: $spacing-01 $spacing-03;
          border-radius: 5px;
          background-color: tint($support-04, 90%);
          color: $support-04;
          @include carbon--type-style("productive-heading-02");
          text-align: center;
          font-weight: 700;
        }
      }

      &.hint {
        .bx--accordion__item--active .bx--accordion__heading {
          background-color: transparent;
        }
        .bx--accordion__heading {
          border-bottom: 1px solid $interactive-03;
        }
        .bx--accordion__title {
          display: flex;
          margin-left: 0;
          color: $interactive-01;
          text-transform: uppercase;
          @include carbon--type-style("productive-heading-01", true);

          &:after {
            right: 0;
          }

          .sls-icon {
            margin-right: $spacing-03;
          }
        }
      }

      &.feedback {
        border: 1px solid $ui-03;
        border-radius: 10px;

        .sls-icon {
          margin-right: $spacing-03;
        }
      }

      &.grading-rubric {
        padding: 0;
      }
    }

    .multiple-choice-option-view {
      .option-set {
        background-color: $ui-01;
      }
      &.correctOption {
        .option-set {
          background-color: tint($state-positive, 90%);
        }
      }

      .field-set {
        &.marks {
          margin-bottom: 0;
        }
      }
    }

    .divider {
      height: 1rem;
      border-top: 1px solid $ui-03;
    }
  }

  .quiz-question {
    > .wrapper {
      > .component-header {
        margin: $spacing-05 0;
        .component-title {
          @include carbon--type-style("productive-heading-03", true);
        }
      }
    }
  }

  .field-set {
    // Instructions for standalone questions and qustions in quiz
    &.instructions {
      padding: $spacing-05;
      border-radius: 10px;
      background-color: $ui-03;

      .field-label {
        @include carbon--type-style("helper-text-01", true);
        display: flex;
        align-items: center;
        margin-bottom: $spacing-02;
        text-transform: uppercase;
        color: $text-02;

        svg {
          margin-right: $spacing-03;
        }
      }

      .content,
      .field-value {
        @include carbon--type-style("label-01", true);
      }
    }
  }
}
</style>
